import { InjectionToken } from '@angular/core';

import {
  AuthFailureCause,
  AuthorizationFailure,
  MalformedResponse,
  RefreshRejection,
  UnsupportedStep,
} from '../model/failure';
import { RefreshTrigger, SignInSource, SignOutReason } from '../model/tokens';
import { AuthorizationErrorSummary } from '../oauth/authorization-request';

/**
 * Durations measured as RUM vitals.
 *
 * A closed set rather than a `string`, so a dashboard cannot be broken by a typo and the names
 * here are greppable from the queries that consume them.
 */
export type AuthDuration = 'auth.session_restore' | 'auth.biometric_sign_in' | 'auth.login' | 'auth.refresh';

export type DurationOutcome = 'success' | 'failure';

/** The redundant Session Restore triggers worth counting. */
export type CoalescedTrigger = 'accessTokenRejected' | 'refreshRequested';

/**
 * How a usable session came to exist.
 *
 * The distinction matters for reading the logout rate: sign-outs are only meaningful against the
 * number of sessions that were established.
 */
export type SessionOrigin = 'signIn' | 'restore' | 'refresh';

/**
 * The facets a duration's close cannot work out for itself, so they are read when it opens.
 *
 * `source` belongs to `auth.login` and `trigger` to `auth.refresh`. A vital carries at most one of
 * them, and `auth.biometric_sign_in` carries neither — the pair is one type because the problem is
 * one problem: a region does not end in the state it began in.
 *
 * A named pair with two closed unions rather than a context bag, so a third facet is a deliberate
 * widening. See the redaction note on {@link AuthTelemetry}.
 */
export interface DurationLabels {
  readonly source?: SignInSource;
  /** Why the Session Restore being measured was started. Its absence on a resume is what made the gate on `appResumed` unmeasurable. */
  readonly trigger?: RefreshTrigger;
}

/**
 * How the enrollment offer was answered.
 *
 * `offered` is emitted when the offer is shown, and is the denominator: an offer nobody accepts is a
 * different problem from an offer nobody sees, and without the shown count the other three are
 * unreadable.
 *
 * `postponed` and `declined` are separate because they write different things — "Maybe later" stores
 * nothing at all and will be asked again, "Don't ask again" is recorded permanently — so a rate that
 * pooled them could not tell a hesitant person from a refusal.
 */
export type BiometricOfferAnswer = 'offered' | 'enrolled' | 'postponed' | 'declined';

/**
 * Which credential-store operation misbehaved.
 *
 * One signal with two operations rather than two signals, because they are one family — "the store did
 * not do what it was asked, here is which part and what it said" — and they want reading together: a
 * device failing writes and a device failing deletes are the same store misbehaving.
 */
export type CredentialStoreOperation = 'save' | 'delete';

/**
 * A failure reduced to the parts that are safe to send off-device.
 *
 * `AuthFailureCause` is already free of secrets, but it is a union that will grow, and the next
 * member added to it should not be able to widen what gets shipped to Datadog by default. This
 * type is the allow-list.
 */
export interface AuthFailureLabel {
  readonly cause: AuthFailureCause['kind'];
  /** Present only for `resourceServerRejected`, where 401-versus-403 is the whole diagnosis. */
  readonly status?: number;
  /**
   * Which of a cause's several origins this was, for the causes that have more than one.
   *
   * This is the answer to "a `refreshFailed` that does not say why", and it is a closed union
   * rather than a free-form message: a label is greppable from a dashboard query, cannot carry a
   * token, and makes a new origin a deliberate widening rather than a new string.
   */
  readonly detail?: AuthFailureDetail;
}

/** Every `detail` a cause can carry. One union, so `AuthFailureLabel` stays flat. */
export type AuthFailureDetail = MalformedResponse | RefreshRejection | AuthorizationFailure | UnsupportedStep;

export function failureLabel(cause: AuthFailureCause): AuthFailureLabel {
  switch (cause.kind) {
    case 'resourceServerRejected':
      return { cause: cause.kind, status: cause.status };
    case 'malformedResponse':
    case 'refreshRejected':
    case 'authorizationFailed':
    case 'unsupportedChallenge':
      return { cause: cause.kind, detail: cause.detail };
    default:
      return { cause: cause.kind };
  }
}

/**
 * Everything the auth machine reports.
 *
 * Every parameter is a label from a closed set, a `SignOutReason`, or a number. Nothing here
 * accepts an `AuthState`, an `AuthEvent`, a `Command`, or a `Record<string, unknown>`. "Never log
 * token values, `codeVerifier`, the `state` nonce, passwords, or raw callback URLs" is a rule this
 * interface enforces rather than one a reviewer has to remember, because a free-form context bag is
 * exactly how a `codeVerifier` reaches a log.
 *
 * The consequence to accept: adding a new field to a telemetry event means widening this
 * interface deliberately.
 */
export interface AuthTelemetry {
  /**
   * One per state change. Enough to reconstruct any session's path through the machine.
   *
   * `failure` is present exactly when the event that caused the transition carried a cause. This is
   * the signal emitted on every state change and so the one anyone debugging reads first, which is
   * why it is worth the extra parameter: a `refreshFailed` row that does not say why sends people to
   * the network panel, and it has done three times.
   */
  transition(from: string, to: string, event: string, failure?: AuthFailureLabel): void;
  /**
   * An event the current state had no transition for. Not an error — this is how we learn which
   * races are frequent enough to be worth modelling.
   *
   * `from` rather than `state`, to agree with {@link transition} above: both name the state an event
   * arrived in, and an adapter that emits them under different field names cannot be grouped on one
   * facet.
   */
  illegal(from: string, event: string): void;
  /** A nonce mismatch or an implausibly old authorization response. Should always be zero. */
  securityEvent(failure: AuthFailureLabel): void;
  /**
   * An authorization callback reported an error instead of a code.
   *
   * **The one deliberate widening of this interface**: the summary carries the identity provider's
   * own `error_description`, which is free-form text. The shape around it is defensive — an
   * allow-list of four fields, a closed union for the code, a length bound on the description, and
   * no path for the code or the nonce. Why the text is carried at all, and the bound on it: see
   * {@link AuthorizationErrorSummary}.
   *
   * Not an alertable error. `access_denied` is someone declining at Google, which is a normal thing
   * to do.
   */
  authorizationError(summary: AuthorizationErrorSummary): void;
  /** A usable session now exists. The denominator for {@link signedOut}. */
  sessionEstablished(origin: SessionOrigin): void;
  /** The session ended. */
  signedOut(reason: SignOutReason): void;
  /** A Session Restore trigger arrived while one was already in flight. Proves single-flight. */
  coalescedRefresh(trigger: CoalescedTrigger): void;
  /**
   * A Command handler threw or rejected without converting the failure into an event.
   *
   * A handler is responsible for reporting its own failures as events, because only it knows
   * whether a failure means `grantFailed` or `refreshFailed`. An escaped error is therefore a
   * bug, and it leaves someone stuck in a transient state — so it gets its own alertable signal
   * rather than being folded into a generic error log.
   */
  commandFailed(command: string): void;
  /**
   * The enrollment offer was shown, or answered.
   *
   * Its own signal rather than a state transition, because the offer is not part of the machine: it is
   * a component the app hosts after a password sign-in, and none of its three answers changes
   * `AuthState`. See {@link BiometricOfferAnswer}.
   */
  biometricOffer(answer: BiometricOfferAnswer): void;
  /**
   * The credential store did not do what it was asked.
   *
   * **Alertable, because nothing else tells anyone the store has stopped working.** A broken delete
   * leaves a password on a device that asked for it to be gone, and nobody is watching a screen when it
   * happens; an unverifiable write leaves a button that raises a prompt and then always fails. A broken
   * *write* does reach somebody, as an enrollment that would not turn on, and is reported here as well —
   * that is the only thing joining it to enrollment rates falling.
   *
   * Deliberately not folded into {@link commandFailed}, which says a Command threw: the write is
   * reported from inside `enrollBiometrics`, which answers with `taskFailed` and so did not throw. Also
   * deliberately not a {@link BiometricOfferAnswer} — these are not answers to an offer.
   *
   * `detail` comes from `errorDetail`, which is deliberate about what it does *not* carry: see that
   * function for why an error from this store must never be serialised whole.
   */
  credentialStoreFailed(operation: CredentialStoreOperation, detail: string): void;
  startDuration(name: AuthDuration): void;
  /**
   * Close a vital.
   *
   * `labels` carries what the close cannot know — which way in a sign-in was, and why a Session
   * Restore was started. Both are read when the vital opens; see {@link DurationLabels}.
   */
  stopDuration(name: AuthDuration, outcome: DurationOutcome, failure?: AuthFailureLabel, labels?: DurationLabels): void;
}

export const AUTH_TELEMETRY = new InjectionToken<AuthTelemetry>('AuthTelemetry');
